import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

const MAX_NAME_LENGTH = 31;

/**
 * Reads the device's current sensor name over serial, lets the operator edit
 * it (up to 31 characters), and writes the new name back via setName. The Save
 * button stays disabled until the field holds a non-empty value that differs
 * from what the device reported.
 */
export default function SensorNameCard({ serialClient, onError = undefined }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const current = await serialClient.getName();
        if (cancelled) return;
        setName(current || '');
        setOriginalName(current || '');
      } catch (err) {
        if (cancelled) return;
        const message = err.message || 'Failed to read sensor name';
        if (onError) onError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serialClient, onError]);

  const handleSave = useCallback(async () => {
    setBusy(true);
    setSuccess(false);
    setError(null);
    try {
      await serialClient.setName(name);
      setOriginalName(name);
      setSuccess(true);
    } catch (err) {
      const message = err.message || 'Failed to save sensor name';
      setError(message);
      if (onError) onError(message);
    } finally {
      setBusy(false);
    }
  }, [serialClient, name, onError]);

  const trimmed = name.trim();
  const unchanged = name === originalName;
  const saveDisabled = busy || loading || trimmed.length === 0 || unchanged;

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Sensor Name
        </Typography>

        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <TextField
            label="Sensor Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="Up to 31 characters"
            inputProps={{ maxLength: MAX_NAME_LENGTH }}
            fullWidth
            sx={{ mb: 2 }}
          />
        )}

        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saveDisabled}
        >
          {busy ? 'Saving...' : 'Save'}
        </Button>

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Sensor name saved.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

SensorNameCard.propTypes = {
  serialClient: PropTypes.object.isRequired,
  onError: PropTypes.func,
};
